import { CONFIG } from '../config/config';
import { hasEarlyAccess } from './earlyAccess';

export function getSaleUnitPriceInr() {
  return hasEarlyAccess() ? CONFIG.PRICE.EARLY_BIRD : CONFIG.PRICE.CURRENT;
}

export function inrToUsdRounded(inr) {
  return Math.round(inr / CONFIG.PRICE.DOLLAR_RATE);
}

export function savingsPerUnitVsOriginal(unitInr) {
  return Math.max(0, CONFIG.PRICE.ORIGINAL - unitInr);
}

export function getDiscountPercent(original, current) {
  if (!original || original <= 0) return 0;
  return Math.round(((original - current) / original) * 100);
}

export function formatInr(amount) {
  return `₹${amount}`;
}

export function formatUsdDisplay(usd) {
  return `$${usd}`;
}

/**
 * Flatten cart lines into per-unit prices (INR).
 * Accepts items with unitPriceInr or legacy `price` (treated as INR if no flag).
 */
export function flattenUnitPrices(items) {
  const units = [];
  for (const item of items) {
    const unit = Number(item.unitPriceInr ?? item.price ?? 0);
    const qty = Number(item.quantity ?? 1);
    for (let i = 0; i < qty; i += 1) {
      units.push(unit);
    }
  }
  return units;
}

/**
 * Buy 3 Get 1: every 4th unit (lowest in each sorted group of 4) is free.
 * When disabled, returns sum of all units.
 */
export function computePayableTotalInr(items) {
  const units = flattenUnitPrices(items);
  if (units.length === 0) return 0;

  if (!CONFIG.SALE.ENABLE_BUY3_GET1) {
    return units.reduce((a, b) => a + b, 0);
  }

  const sorted = [...units].sort((a, b) => a - b);
  let total = 0;
  for (let i = 0; i < sorted.length; i += 1) {
    if (i % 4 !== 0) {
      total += sorted[i];
    }
  }
  return total;
}

export function computeListTotalAtCurrentInr(items) {
  return flattenUnitPrices(items).reduce((a, b) => a + b, 0);
}

export function computeBuy3Get1DiscountInr(items) {
  if (!CONFIG.SALE.ENABLE_BUY3_GET1) return 0;
  const list = computeListTotalAtCurrentInr(items);
  const payable = computePayableTotalInr(items);
  return Math.max(0, list - payable);
}

/** Value if every unit were sold at ORIGINAL (for savings messaging). */
export function computeListTotalAtOriginalInr(itemCount) {
  return CONFIG.PRICE.ORIGINAL * itemCount;
}

export function computeTotalSavingsVsOriginalInr(items) {
  const n = flattenUnitPrices(items).length;
  if (n === 0) return 0;
  const originalList = computeListTotalAtOriginalInr(n);
  const payable = computePayableTotalInr(items);
  return Math.max(0, originalList - payable);
}

export function getPricePresentation() {
  const early = hasEarlyAccess();
  const currentInr = early ? CONFIG.PRICE.EARLY_BIRD : CONFIG.PRICE.CURRENT;
  const usd = inrToUsdRounded(currentInr);
  const savedInr = savingsPerUnitVsOriginal(currentInr);
  const discountPercent = getDiscountPercent(CONFIG.PRICE.ORIGINAL, currentInr);
  return {
    earlyAccess: early,
    originalInr: CONFIG.PRICE.ORIGINAL,
    currentInr,
    usd,
    savedInr,
    discountPercent,
    savedLabel: `You saved ${formatInr(savedInr)}`,
  };
}
